import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import { TextField, Switch, CheckboxWithLabel, Checkbox } from 'formik-mui';

import moment from 'moment';
import { makeStyles, useTheme } from '@mui/styles';
import {
  Card,
  CardContent,
  Grid,
  MenuItem,
  FormControlLabel,
  FormGroup,
  Button,
  Typography,
  Box,
  FormControl,
} from '@mui/material';

import { fetchOptions } from '../../store/options';
import { getOptionsState } from '../../store/options/selectors';
import { fetchCategories } from '../../store/categories';
import { getCategoriesState } from '../../store/categories/selectors';
import { fetchDietaryRequirements } from '../../store/dietaryRequirements';
import { getDietaryRequirementsState } from '../../store/dietaryRequirements/selectors';

import Options from './Options';
import AddCategory from '../../pages/Categories/AddCategory';
import PhotoUploader from '../PhotoUploader';
import { getDiscountsState } from '../../store/itemDiscounts/selectors';
import { fetchDiscounts } from '../../store/itemDiscounts';
import DiscountDialogue from '../../pages/Discounts/DiscountDialogue';
import UniversalSave from '../UniversalSave';
import Page from '../Page';
import CustomDialog from '../CustomDialog';
import ResetCircle from '../ResetCircle';

const checkList = (list, array) => {
  const matches = [];
  list.forEach((item) => {
    if (array.includes(item)) {
      matches.push(item);
    }
  });
  return matches;
};

const getNameFromRequirementId = (id, dietaryRequirements) => {
  const returnName = dietaryRequirements.find((requirement) => requirement.requirementId === id);

  return returnName.name;
};

const hasConflict = (requirement, array) => array.includes(requirement) && array.length > 1;

const getAllergenConflictMessage = (matches, value, dietaryRequirements, title) => {
  if (!value || value.length === 0) return [false];
  const matchingList = checkList(matches, value);
  const matchingNames = matchingList
    .slice(1)
    .map((item) => getNameFromRequirementId(item, dietaryRequirements))
    .join(', ');
  const conflict = hasConflict(matches[0], matchingList);
  const message = `You cannot use the dietary preference ${title} and the allergens ‘${matchingNames}’.`;
  return [conflict, message];
};

// eslint-disable-next-line func-names
const dietaryConflicts = function (errorMessage, dietaryRequirements) {
  // eslint-disable-next-line func-names
  return this.test(`test-dietary-conflicts`, errorMessage, function (value) {
    const { path, createError } = this;
    const [dairyConflict, dairyMessage] = getAllergenConflictMessage(
      ['34', '8'],
      value,
      dietaryRequirements,
      'Dairy free',
    );
    const [veganConflict, veganMessage] = getAllergenConflictMessage(
      ['32', '33', '9', '5', '4', '3', '8'],
      value,
      dietaryRequirements,
      'Vegan',
    );
    const [vegetarianConflict, vegetarianMessage] = getAllergenConflictMessage(
      ['33', '9', '5', '3'],
      value,
      dietaryRequirements,
      'Vegetarian',
    );
    const [glutenConflict, glutenMessage] = getAllergenConflictMessage(
      ['35', '6', '29', '30', '26', '23', '21', '27'],
      value,
      dietaryRequirements,
      'Gluten free',
    );

    const message =
      (dairyConflict && dairyMessage) ||
      (veganConflict && veganMessage) ||
      (vegetarianConflict && vegetarianMessage) ||
      (glutenConflict && glutenMessage);

    return (
      (!dairyConflict && !veganConflict && !vegetarianConflict && !glutenConflict) ||
      createError({ path, message })
    );
  });
};

Yup.addMethod(Yup.array, 'dietaryConflicts', dietaryConflicts);

const imageApprovalDescriptionMap = {
  yes: null,
  pending: {
    title: 'Pending',
    content: 'Images will be reviewed within 24hrs.',
  },
  no: {
    title: 'Failed',
    content:
      'Your image was not approved, please try uploading a new one or select one from our gallery.',
  },
};

const useStyles = makeStyles((theme) => ({
  availableSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: '15px',
    marginBottom: '15px',
    borderBottom: '1px solid #f0f4f8',
  },
  labelText: {
    color: '#757575',
    marginLeft: '-10px',
  },
  section: {
    paddingBottom: '15px',
    marginBottom: '15px',
    borderBottom: '1px solid #f0f4f8',
  },
  cardActions: {
    justifyContent: 'flex-end',
  },
  dietIcon: {
    width: 20,
    height: 20,
    marginRight: 10,
  },
  title: { marginBottom: '15px' },
  imgPreview: {
    width: 200,
  },
  error: {
    color: 'red !important',
  },
  discountOptions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  discountOptionsPrices: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  discountOriginalPrice: {
    textDecoration: 'line-through',
    color: '#647F99',
  },
}));

const MenuItemForm = ({
  initialValues,
  onSubmit,
  onReorderOptions,
  readOnlyFields,
  refreshItem,
  enforceImageRequirements,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();

  const formikRef = useRef();
  const dietaryRequirementsState = useSelector(getDietaryRequirementsState);
  const optionsState = useSelector(getOptionsState);
  const categoriesState = useSelector(getCategoriesState);
  const discountsState = useSelector(getDiscountsState);
  const { data: dietaryRequirements } = dietaryRequirementsState;
  const { data: options } = optionsState;
  const { data: categories } = categoriesState;
  const { data: itemDiscounts } = discountsState;
  const loading = dietaryRequirementsState.loading || categoriesState.loading;
  const error = dietaryRequirementsState.error || optionsState.error || categoriesState.error;
  const [newCategory, setNewCategory] = useState(null);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isDiscountDialogOpen, setIsDiscountDialogOpen] = useState(false);
  const [useNameAsLabel, setUseNameAsLabel] = useState(
    initialValues.label === initialValues.itemName,
  );

  const theme = useTheme();

  const refreshDiscount = async () => {
    await dispatch(fetchDiscounts());
  };

  const getDiscountedPrices = (values, amount) => (
    <>
      {values.itemOptions.map(
        ({ optionName, optionPrice }) =>
          optionPrice && (
            <div key={optionName} className={classes.discountOptions}>
              <span>{optionName}</span>
              <div className={classes.discountOptionsPrices}>
                <span className={classes.discountOriginalPrice}>£{optionPrice.toFixed(2)}</span>
                <span>£{(optionPrice * ((100 - amount) / 100)).toFixed(2)}</span>
              </div>
            </div>
          ),
      )}
    </>
  );
  useEffect(() => {
    dispatch(fetchDietaryRequirements());
    dispatch(fetchOptions());
    dispatch(fetchCategories());
    dispatch(fetchDiscounts());
  }, [dispatch]);

  useEffect(() => {
    if (
      !isCategoryDialogOpen &&
      newCategory?.categoryName &&
      categories.findIndex((e) => e.categoryName === newCategory?.categoryName) !== -1
    ) {
      if (formikRef.current) {
        formikRef.current.setFieldValue('category', newCategory?.categoryName);
        formikRef.current.setFieldValue('type', newCategory?.type);
      }
    }
  }, [newCategory, categories, isCategoryDialogOpen, initialValues]);

  const handleCloseCategoryDialog = (newCat) => {
    dispatch(fetchCategories());
    setNewCategory(newCat);
    setIsCategoryDialogOpen(false);
  };

  const getCategoryType = (value) =>
    categories.find((category) => category.categoryName === value).type;

  const MenuItemSchema = Yup.object().shape({
    itemName: Yup.string().min(3, 'Too Short!').required('Please enter a name'),
    label: Yup.string().min(3, 'Too Short!').required('Please enter a label'),
    category: Yup.string().required('Please choose a category'),
    discountId: Yup.string(),
    isAlcoholic: Yup.boolean().required(),
    dietaryRequirements: Yup.array()
      .of(Yup.string())
      .dietaryConflicts('Conflicts with other dietary requirements', dietaryRequirements),
    vatRate: Yup.number().min(0).max(25).required('Please enter a vat rate'),
    itemOptions: Yup.array()
      .of(
        Yup.object().shape({
          optionName: Yup.string().required('Please choose a size'),
          optionPrice: Yup.number()
            .min(0, 'Price must be greater than 0')
            .typeError('Price must be a number')
            .required('Please choose a price for this size'),
          optionCalories: Yup.number()
            .min(0, 'Calories must be greater than or equal to 0')
            .notRequired()
            .default(undefined),
        }),
      )
      .min(1, 'Please choose at least one size'),
  });
  const labelText = (values) => {
    if (values.snoozeUntil && !values.available) {
      return `until ${moment(values.snoozeUntil).local().format('DD/MM/YYYY HH:mm')}`;
    }
    return null;
  };

  const labelTitle = (values) => {
    if (values.available) {
      return 'Available';
    }
    if (values.snoozeUntil && !values.available) {
      return 'Snoozed';
    }
    return 'Unavailable';
  };

  return (
    <Page loading={loading} error={error} fullWidth>
      <Card className={classes.card}>
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          onReset={() => setUseNameAsLabel(initialValues.label === initialValues.itemName)}
          readOnlyFields={readOnlyFields}
          validationSchema={MenuItemSchema}
        >
          {({
            values,
            dirty,
            isValid,
            errors,
            resetForm,
            setFieldValue,
            handleChange,
            setFieldTouched,
          }) => {
            const imageApprovalDescription =
              imageApprovalDescriptionMap[values.imageApprovalStatus];
            return (
              <>
                <UniversalSave
                  isValid={isValid}
                  errors={errors}
                  dirty={dirty}
                  onSave={() => onSubmit(values)}
                  onDiscard={resetForm}
                />
                <Form>
                  <CardContent>
                    <section>
                      <FormGroup className={classes.availableSection}>
                        <FormControlLabel
                          control={
                            <Field
                              component={Switch}
                              color="primary"
                              type="checkbox"
                              name="available"
                              disabled={readOnlyFields?.includes('available')}
                            />
                          }
                          label={
                            <Typography variant="body2" component="h3">
                              {labelTitle(values)}
                            </Typography>
                          }
                        />
                        <Typography className={classes.labelText} variant="caption" component="h6">
                          {labelText(values)}
                        </Typography>
                      </FormGroup>
                      <FormGroup className={classes.section}>
                        <FormControlLabel
                          control={
                            <Field
                              component={Checkbox}
                              color="primary"
                              type="checkbox"
                              name="isAlcoholic"
                              disabled={initialValues.readonly}
                            />
                          }
                          label="Alcoholic"
                        />
                      </FormGroup>
                    </section>
                    {categories && (
                      <section>
                        <FormGroup className={classes.section}>
                          <Field
                            component={TextField}
                            select
                            fullWidth
                            name="category"
                            label="Select Category"
                            variant="outlined"
                            disabled={
                              initialValues.readonly || readOnlyFields?.includes('category')
                            }
                            margin="normal"
                            required
                            onChange={({ target }) => {
                              if (target.value === 'create') {
                                setIsCategoryDialogOpen(true);
                                return;
                              }
                              const categoryType = getCategoryType(target.value);
                              const suggestedVatRate = categoryType === 'FOOD' ? 20 : 20;

                              setFieldValue('category', target.value);
                              setFieldValue('type', categoryType);
                              setFieldValue('vatRate', suggestedVatRate);
                            }}
                          >
                            {categories.map((category) => (
                              <MenuItem value={category.categoryName} key={category.categoryId}>
                                {category.categoryName}
                              </MenuItem>
                            ))}
                            <MenuItem value="create" key="New Category">
                              + New Category
                            </MenuItem>
                          </Field>
                          <Field fullWidth component={TextField} name="type" disabled />
                        </FormGroup>
                      </section>
                    )}
                    <FormGroup className={classes.section}>
                      <Field
                        component={TextField}
                        name="label"
                        label="Item name"
                        variant="outlined"
                        fullWidth
                        required
                        disabled={initialValues.readonly || readOnlyFields?.includes('label')}
                        margin="normal"
                        value={values.label}
                        onChange={({ target }) => {
                          if (useNameAsLabel) {
                            setFieldValue('itemName', target.value);
                          }
                          setFieldValue('label', target.value);
                        }}
                      />
                      <Box>
                        <Field
                          component={TextField}
                          name="itemName"
                          label="Internal name (kitchen ticket)"
                          variant="outlined"
                          fullWidth
                          disabled={initialValues.readonly || readOnlyFields?.includes('itemName')}
                          required
                          margin="normal"
                          value={values.itemName}
                        />
                        {location.pathname !== '/menu-items/add' &&
                          values.label !== values.itemName && (
                            <ResetCircle
                              brandFieldValue="itemName"
                              initialDefaultValue={values.label}
                              setFieldValue={setFieldValue}
                              disabled={
                                initialValues.readonly || readOnlyFields?.includes('itemName')
                              }
                              altText="Reset kitchen ticket name"
                              tooltipTitle="Reset to item name"
                              right="16px"
                            />
                          )}
                      </Box>
                      <Field
                        component={TextField}
                        name="description"
                        label="Description"
                        variant="outlined"
                        fullWidth
                        multiline
                        disabled={initialValues.readonly || readOnlyFields?.includes('description')}
                        margin="normal"
                        // required
                      />
                      <Field
                        component={TextField}
                        name="vatRate"
                        label="Vat Rate (%)"
                        variant="outlined"
                        type="number"
                        InputProps={{ inputProps: { min: 0, max: 25, step: '0.1' } }}
                        disabled={initialValues.readonly || readOnlyFields?.includes('vatRate')}
                        margin="normal"
                        // required
                      />
                    </FormGroup>
                    <FormGroup className={classes.section}>
                      <Box display="flex" justifyContent="space-between">
                        <div>
                          <Typography className={classes.title} variant="h4">
                            Product photo
                          </Typography>
                          <PhotoUploader
                            disabled={initialValues.readonly}
                            hasImage={values.hasImage}
                            imgUrl={values.imgUrl}
                            originalImgUrl={values.originalImgUrl}
                            imgUploadUrl={values.imgUploadUrl}
                            imgUploadUrlOriginal={values.imgOriginalUploadUrl}
                            refresh={refreshItem}
                            itemId={values.itemId}
                            imageApprovalStatus={values.imageApprovalStatus}
                            enforceImageRequirements={enforceImageRequirements}
                          />
                          {imageApprovalDescription && (
                            <Box my={2}>
                              <Typography>
                                <strong>Upload status</strong>: {imageApprovalDescription.title}
                              </Typography>
                              <Typography>{imageApprovalDescription.content}</Typography>
                            </Box>
                          )}
                        </div>
                      </Box>
                    </FormGroup>
                    <FormGroup className={classes.section} sx={{ borderBottom: 'none !important' }}>
                      <Typography className={classes.title} variant="h4">
                        Dietary Preferences
                      </Typography>
                      <FormControl
                        disabled={initialValues.readonly}
                        component="fieldset"
                        type="checkbox"
                        color="primary"
                        style={{
                          justifyContent: 'flex-start',
                          flexFlow: 'row wrap',
                          width: '100%',
                        }}
                      >
                        {dietaryRequirements &&
                          dietaryRequirements.length > 0 &&
                          dietaryRequirements.map(
                            ({ name, type, requirementId, abbreviation }) =>
                              type === 'requirement' && (
                                <div style={{ minWidth: '200px' }} key={requirementId}>
                                  <Field
                                    type="checkbox"
                                    component={CheckboxWithLabel}
                                    name="dietaryRequirements"
                                    value={requirementId}
                                    disabled={initialValues.readonly}
                                    onChange={(e) => {
                                      setFieldTouched('dietaryRequirements', true);
                                      handleChange(e);
                                    }}
                                    Label={{
                                      label: `${name}${abbreviation && `(${abbreviation})`}`,
                                    }}
                                  />
                                </div>
                              ),
                          )}
                      </FormControl>
                    </FormGroup>
                    <FormGroup className={classes.section}>
                      <Typography className={classes.title} variant="h4">
                        Contains Allergens
                      </Typography>
                      <FormControl
                        disabled={initialValues.readonly}
                        component="fieldset"
                        type="checkbox"
                        color="primary"
                        style={{
                          justifyContent: 'flex-start',
                          flexFlow: 'row wrap',
                          width: '100%',
                        }}
                      >
                        {dietaryRequirements &&
                          dietaryRequirements
                            .filter(({ type }) => type === 'allergen')
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(({ name, requirementId }) => (
                              <Grid item xs={12} md={6} lg={4} key={requirementId}>
                                <Field
                                  disabled={initialValues.readonly}
                                  type="checkbox"
                                  component={CheckboxWithLabel}
                                  name="dietaryRequirements"
                                  value={requirementId}
                                  Label={{ label: name }}
                                  onChange={(e) => {
                                    setFieldTouched('dietaryRequirements', true);
                                    handleChange(e);
                                  }}
                                />
                              </Grid>
                            ))}
                      </FormControl>
                    </FormGroup>
                    <div style={{ color: theme.palette.error.main }}>
                      <ErrorMessage name="dietaryRequirements" component="div" />
                    </div>
                    {itemDiscounts && (
                      <section>
                        <FormGroup className={classes.section}>
                          <Field
                            disabled={initialValues.readonly}
                            component={TextField}
                            select
                            fullWidth
                            name="discountId"
                            label="Select Discount"
                            variant="outlined"
                            margin="normal"
                            onChange={({ target }) => {
                              if (target.value === 'create') {
                                setIsDiscountDialogOpen(true);
                                return;
                              }
                              setFieldValue('discountId', target.value);
                            }}
                          >
                            {itemDiscounts &&
                              itemDiscounts.map(({ discountId, name, label }) => (
                                <MenuItem value={discountId} key={discountId}>
                                  {name} ({label})
                                </MenuItem>
                              ))}
                            <MenuItem value="create" key="New Discount">
                              + New discount
                            </MenuItem>
                          </Field>
                          {values.discountId &&
                            itemDiscounts.find(
                              (discount) => discount.discountId === values.discountId,
                            ).status === 'Active' &&
                            getDiscountedPrices(
                              values,
                              itemDiscounts.find(
                                ({ discountId }) => discountId === values.discountId,
                              ).amount,
                            )}
                          <Box>
                            <Button
                              disabled={initialValues.readonly}
                              type="button"
                              variant="outlined"
                              style={{ marginTop: theme.spacing(2) }}
                              onClick={() => {
                                setFieldValue('discountId', '');
                              }}
                            >
                              Remove discount
                            </Button>
                          </Box>
                        </FormGroup>
                      </section>
                    )}

                    <br />
                    {options && (
                      <Options
                        readonly={initialValues.readonly}
                        setFieldValue={setFieldValue}
                        itemOptions={values.itemOptions}
                        allowReordering={
                          !initialValues.readonly && !dirty && values.itemOptions.length > 1
                        }
                        onReorder={onReorderOptions}
                      />
                    )}
                  </CardContent>

                  <DiscountDialogue
                    isOpen={isDiscountDialogOpen}
                    setIsOpen={setIsDiscountDialogOpen}
                    onSubmit={async (val) => {
                      await refreshDiscount(val);
                      setFieldValue('discountId', val.discountId);
                    }}
                  />
                </Form>
              </>
            );
          }}
        </Formik>
      </Card>
      <CustomDialog
        title="Add a new category"
        isDialogOpen={isCategoryDialogOpen}
        handleCloseDialog={handleCloseCategoryDialog}
      >
        <AddCategory
          modalSubmit={handleCloseCategoryDialog}
          modalClose={handleCloseCategoryDialog}
        />
      </CustomDialog>
    </Page>
  );
};

export default MenuItemForm;
